<script setup>
// NOTICE: KAPTION-INTEGRATION — note-cards.GROUP
// A group of back-to-back events as one line: the badges of its cards, the title of its lead card
// (or the text of its last activity line), how many more events it holds and when. Opening it shows
// every event in place, each as it shows on its own (the default slot).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import {
  NOTE_CARD_AGENT_EDGE,
  noteCardTone,
  noteCardBadgeClass,
  noteCardBadgeIcon,
} from 'dashboard/helper/kaptionNoteCards';
import { activityMessageText } from 'dashboard/helper/activityMessageText';

const props = defineProps({
  // summarizeEventGroup(...) in dashboard/helper/kaptionNoteCardGroups
  summary: {
    type: Object,
    required: true,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle']);

const { t, te } = useI18n();

// note-cards.GROUP.3 — the badges of its cards, in order
const badges = computed(() =>
  props.summary.badgeCards.map(card => {
    const tone = noteCardTone(card);
    return {
      className: noteCardBadgeClass(tone),
      icon: noteCardBadgeIcon(card, tone),
    };
  })
);

const leadCard = computed(() => props.summary.leadCard);
const titleClass = computed(() =>
  leadCard.value && noteCardTone(leadCard.value) === 'danger'
    ? 'text-n-ruby-11'
    : 'text-n-slate-12'
);

// note-cards.GROUP.4 — a group with no card reads as its last activity line
const activityText = computed(() => {
  const message = props.summary.lastActivity;
  if (!message) return '';
  return activityMessageText(
    { content: message.content, contentAttributes: message.contentAttributes },
    { t, te }
  );
});

const timeSpan = computed(() => {
  const start = messageTimestamp(props.summary.startAt, 'h:mm a');
  const end = messageTimestamp(props.summary.endAt, 'h:mm a');
  return start === end ? end : `${start}–${end}`;
});

const moreLabel = computed(() =>
  props.summary.more === 1
    ? t('CONVERSATION.NOTE_CARDS.GROUP.MORE_ONE')
    : t('CONVERSATION.NOTE_CARDS.GROUP.MORE_MANY', {
        count: props.summary.more,
      })
);

const moreBadge = computed(() => `+${props.summary.more}`);

const chevron = computed(() =>
  props.isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
);
</script>

<template>
  <div
    class="mb-2 flex w-full flex-col items-center gap-1.5"
    data-test="note-card-group"
  >
    <!-- note-cards.GROUP.3 / note-cards.GROUP.6 -->
    <button
      v-if="summary.hasCards"
      type="button"
      class="inline-flex max-w-full items-center gap-2 rounded-full bg-n-solid-1 py-1 pe-2.5 ps-1 text-[13px] leading-5 text-n-slate-12"
      :class="NOTE_CARD_AGENT_EDGE"
      :aria-expanded="isOpen ? 'true' : 'false'"
      data-test="note-card-group-line"
      @click="emit('toggle')"
    >
      <span class="inline-flex shrink-0 items-center">
        <span
          v-for="(badge, position) in badges"
          :key="position"
          class="inline-flex size-5 shrink-0 items-center justify-center rounded-full ring-2 ring-n-solid-1"
          :class="[badge.className, { '-ms-1.5': position > 0 }]"
          data-test="note-card-group-badge"
        >
          <span class="size-3" :class="badge.icon" />
        </span>
      </span>
      <span
        class="min-w-0 truncate font-semibold"
        :class="titleClass"
        data-test="note-card-group-title"
      >
        {{ leadCard.title }}
      </span>
      <span
        class="shrink-0 rounded-full bg-[#EFE9FD] px-1.5 text-xs font-semibold leading-[18px] text-[#6D28D9] dark:bg-[#2A2146] dark:text-[#B99DFF]"
        data-test="note-card-group-more"
      >
        <span aria-hidden="true">{{ moreBadge }}</span>
        <span class="sr-only">{{ moreLabel }}</span>
      </span>
      <span class="shrink-0 text-n-slate-10">{{ timeSpan }}</span>
      <span class="size-3.5 shrink-0 text-n-slate-11" :class="chevron" />
    </button>

    <!-- note-cards.GROUP.4 -->
    <button
      v-else
      type="button"
      class="inline-flex min-w-0 max-w-full items-center gap-2 rounded-xl bg-n-alpha-1 px-3 py-1 text-sm text-n-slate-11"
      :aria-expanded="isOpen ? 'true' : 'false'"
      data-test="note-card-group-line"
      @click="emit('toggle')"
    >
      <span
        v-dompurify-html="activityText"
        class="min-w-0 truncate"
        data-test="note-card-group-title"
      />
      <span
        class="shrink-0 rounded-full bg-n-alpha-2 px-1.5 text-xs font-semibold leading-[18px]"
        data-test="note-card-group-more"
      >
        <span aria-hidden="true">{{ moreBadge }}</span>
        <span class="sr-only">{{ moreLabel }}</span>
      </span>
      <span class="size-3.5 shrink-0" :class="chevron" />
    </button>

    <!-- note-cards.GROUP.5 — every event in place, as it shows on its own -->
    <div
      v-if="isOpen"
      class="flex w-full flex-col rounded-2xl border border-dashed px-2 pt-2"
      :class="
        summary.hasCards
          ? 'border-[rgba(124,58,237,0.30)] bg-[rgba(124,58,237,0.04)] dark:border-[rgba(167,139,250,0.35)] dark:bg-[rgba(124,58,237,0.08)]'
          : 'border-n-slate-6 bg-n-alpha-1'
      "
      data-test="note-card-group-events"
    >
      <slot />
    </div>
  </div>
</template>
